import { Typography, TextField, Box, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Controller, useForm } from 'react-hook-form';
import { WAutocomplete, WButton } from '@wareclouds/wareclouds-ui';
import { COUNTRY_CONFIG } from '../../domain';
import { useBankAccountForm } from '../../application';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CONTEXT } from '@/config/consts/commons';
import { useBankAccountFormSyncCl } from '../hooks/useBankAccountFormSyncCl';
import { formatRut } from '@wareclouds/frontend-common/b2c';
import { BankAccountFormSkeleton } from './BankAccountFormSkeleton';

const inputStyles = {
  fontSize: '1.2rem',
  '& .MuiInputBase-root': { height: '47px' },
};

const responsiveRowSpacing = { xs: 3, md: 5 };

const gridStyle = { xs: 12, md: 6 };

export const BankAccountFormCl = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['form', 'validation']);
  const countryConfig = COUNTRY_CONFIG[CONTEXT];

  const {
    banksData,
    isLoadingDataBankList,
    isSavingData,
    existingBankData,
    isLoadingExistingData,
    onSubmit,
  } = useBankAccountForm({ countryConfig });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(countryConfig.createSchema(t)),
    defaultValues: countryConfig.initialValues,
    mode: 'onChange',
  });

  const selectedBank = watch('bank');
  const selectedAccountType = watch('accountType');

  const { isLoadBlocking, isSaveDisabled, isLoadingBankData } =
    useBankAccountFormSyncCl({
      existingBankData,
      banksData,
      selectedBank,
      selectedAccountType,
      isSubmitting,
      isSavingData,
      isLoadingExistingData,
      isLoadingDataBankList,
      countryConfig,
      reset,
      setValue,
    });

  if (isLoadingExistingData || isLoadingDataBankList) {
    return <BankAccountFormSkeleton fieldCount={4} />;
  }

  return (
    <>
      <Box mb={3}>
        <Typography
          variant="body1"
          component="h1"
          mb={2}
          sx={{ fontWeight: 400, fontSize: 24 }}
        >
          {t('bank_info.title')}
        </Typography>
        <Typography mb={2} component="p">
          {t('bank_info.description')}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container rowSpacing={responsiveRowSpacing} columnSpacing={4}>
          <Grid size={gridStyle}>
            <Controller
              name="rut"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('bank_info.rut.label')}
                  placeholder={t('bank_info.rut.placeholder')}
                  variant="standard"
                  fullWidth
                  disabled={isLoadingBankData || isSavingData || isLoadBlocking}
                  onChange={(e) => {
                    const formattedRUT = formatRut(e.target.value);
                    field.onChange(formattedRUT);
                  }}
                  error={!!errors.rut}
                  helperText={errors.rut?.message}
                  sx={inputStyles}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          </Grid>

          <Grid size={gridStyle}>
            <WAutocomplete
              name="bank"
              control={control}
              options={banksData || []}
              loading={isLoadingBankData}
              textFieldProps={{
                label: t('bank_info.bank.label'),
                placeholder: t('bank_info.bank.placeholder'),
                variant: 'standard',
                fullWidth: true,
                sx: inputStyles,
                InputLabelProps: { shrink: true },
              }}
              autocompleteProps={{
                disabled: isLoadingBankData || isLoadBlocking,
                isOptionEqualToValue: (option, value) =>
                  option.label === value?.label,
              }}
              translateError={(error) => error?.message}
            />
          </Grid>

          <Grid size={gridStyle}>
            <WAutocomplete
              name="accountType"
              control={control}
              options={selectedBank?.accountTypes || []}
              loading={isLoadingBankData}
              textFieldProps={{
                label: t('bank_info.account_type.label'),
                placeholder: t('bank_info.account_type.placeholder'),
                variant: 'standard',
                fullWidth: true,
                sx: inputStyles,
                InputLabelProps: { shrink: true },
              }}
              autocompleteProps={{
                disabled: isLoadingBankData || isLoadBlocking || !selectedBank,
                isOptionEqualToValue: (option, value) =>
                  option.label === value?.label,
              }}
              translateError={(error) => error?.message}
            />
          </Grid>

          <Grid size={gridStyle}>
            <Controller
              name="accountNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('bank_info.account_number.label')}
                  placeholder={t('bank_info.account_number.placeholder')}
                  variant="standard"
                  fullWidth
                  disabled={isLoadingBankData || isSavingData || isLoadBlocking}
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber?.message}
                  sx={inputStyles}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Stack
          mt={6}
          direction={{ xs: 'column-reverse', md: 'row' }}
          spacing={2}
          justifyContent={{ md: 'space-between' }}
        >
          <WButton
            label={t('button:back')}
            color="secondary"
            variant="contained"
            sx={{
              color: '#000',
              bgcolor: 'grey.300',
              '&:hover': {
                bgcolor: 'grey.300',
              },
              width: { xs: '100%', md: '201px' },
            }}
            onClick={() => navigate('/')}
          />
          <WButton
            label={isSavingData ? t('button:saving') : t('button:save')}
            variant="contained"
            type="submit"
            isDisabled={isSaveDisabled || !isDirty}
            sx={{ width: { xs: '100%', md: '201px' } }}
          />
        </Stack>
      </form>
    </>
  );
};
